import React from "react";
import { theme } from "../ui";
import { useUiLanguage } from "../ui/uiText";
import { spacing, typography } from "../layout/spacing";
import { criticalIssueText, SINGULAR_COUNT, translateCriticalIssue } from "./criticalIssueTranslations";
import { subscribeCriticalIssuesRefresh } from "./criticalIssuesRefresh";

const FAILURE_CIRCUITS_BLOCKED_TYPE = "failure_circuits_blocked";

export type CriticalIssueSeverity = "info" | "warning" | "critical";

export interface CriticalIssueDto {
  id?: string;
  code: string;
  type?: string;
  severity: CriticalIssueSeverity;
  scope?: string;
  title: string;
  message: string;
  startedAt?: string | null;
  finishedAt?: string | null;
  technicalDetail?: string | null;
  occurredAt?: string | null;
  /** Werte für den übersetzten Text (Anzahl, Quellen). */
  parameters?: Record<string, string> | null;
  /** Wohin der Hinweis führt: `failure-circuits`, `incidents`. */
  target?: string | null;
}

export interface GlobalCriticalIssuesBannerProps {
  endpoint?: string;
  pollIntervalMs?: number;
  className?: string;
  /**
   * Eigener Abrufweg. Nötig überall dort, wo der Zugang am HTTP-Modul der App hängt (Token im
   * Kopf statt Cookie) — ein rohes `fetch` bekäme dort nur eine Abweisung. Ohne diese Prop bleibt
   * es beim einfachen Abruf des Endpunkts.
   */
  loadIssues?: () => Promise<CriticalIssueDto[]>;
  /**
   * Wechselt der Wert, wird sofort neu abgerufen. Damit hängt die Anzeige an den Live-Ereignissen
   * der App statt allein am Takt — der Takt bleibt das Sicherheitsnetz.
   */
  reloadSignal?: unknown;
  /**
   * Sprung in den Bereich, der zur Meldung gehört (Reiter „Störungen“ bzw. „Fehlerfälle“). Ohne
   * diese Prop bleibt der Hinweis ein reiner Hinweis.
   */
  onOpenTarget?: (target: string) => void;
}

const defaultEndpoint = "/api/v1/system/critical-issues";

/**
 * Farbe nach Schwere. Rot bedeutet: Es geht nichts mehr. Gelb: Der Betrieb läuft, aber ein Teil
 * steht still — eine gesperrte Fehlerquelle etwa. Beides blinkt, weil beides gesehen werden muss;
 * die Farbe unterscheidet, wie dringend es ist.
 */
const severityColors: Record<CriticalIssueSeverity, string> = {
  critical: "#ff0000",
  warning: "#ffcc00",
  info: "#ffcc00",
};

const severityRank: Record<CriticalIssueSeverity, number> = {
  critical: 3,
  warning: 2,
  info: 1,
};

/*
 * Gleiches Blinkverhalten wie der m1member-Migrationsbanner (global.css,
 * migration-failure-border-blink): harter An/Aus-Impuls von je 0,5 s, nur der Rahmen,
 * kein Glow — der Inhalt bleibt jederzeit lesbar.
 */
function buildBlinkStyles(color: string) {
  return `
@keyframes mq-critical-issue-blink {
  0%, 49.999% {
    border-color: var(--mq-critical-issue-color, ${color});
  }
  50%, 100% {
    border-color: var(--mq-critical-issue-bg, #000000);
  }
}
.mq-critical-issue-frame {
  animation: mq-critical-issue-blink 1s steps(1, end) infinite;
}
@media (prefers-reduced-motion: reduce) {
  .mq-critical-issue-frame {
    animation: none;
  }
}
`;
}

/** Einfacher Abruf ohne Zugangsdaten — trägt dort, wo die Sitzung am Cookie hängt. */
async function fetchIssues(endpoint: string): Promise<CriticalIssueDto[]> {
  const response = await fetch(endpoint, { cache: "no-store" });
  if (!response.ok) return [];
  return (await response.json()) as CriticalIssueDto[];
}

export function GlobalCriticalIssuesBanner({
  endpoint = defaultEndpoint,
  pollIntervalMs = 15_000,
  className,
  loadIssues,
  reloadSignal,
  onOpenTarget,
}: GlobalCriticalIssuesBannerProps) {
  const [issues, setIssues] = React.useState<CriticalIssueDto[]>([]);
  const language = useUiLanguage();
  const {
    token: { colorBorderSecondary },
  } = theme.useToken();

  // Der Abrufweg darf den Takt nicht neu starten: Eine bei jedem Rendern neu gebildete Funktion
  // würde den Effekt sonst endlos aufsetzen und abbrechen.
  const loadIssuesRef = React.useRef(loadIssues);
  React.useEffect(() => { loadIssuesRef.current = loadIssues; }, [loadIssues]);

  React.useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;
    let requestVersion = 0;

    async function poll() {
      const version = ++requestVersion;
      try {
        const nextIssues = loadIssuesRef.current
          ? await loadIssuesRef.current()
          : await fetchIssues(endpoint);
        if (!cancelled && version === requestVersion) setIssues(Array.isArray(nextIssues) ? nextIssues : []);
      } catch {
        if (!cancelled && version === requestVersion) setIssues([]);
      } finally {
        if (!cancelled && version === requestVersion) timer = window.setTimeout(poll, pollIntervalMs);
      }
    }

    const refresh = () => {
      if (timer != null) window.clearTimeout(timer);
      timer = null;
      void poll();
    };
    const unsubscribe = subscribeCriticalIssuesRefresh(refresh);
    refresh();
    return () => {
      cancelled = true;
      unsubscribe();
      if (timer != null) window.clearTimeout(timer);
    };
  }, [endpoint, pollIntervalMs, reloadSignal]);

  if (issues.length === 0) return null;

  // Die schwerste Meldung bestimmt Farbe und Kopfzeile; die übrigen stehen darunter, damit ein
  // zweiter Vorfall neben einem ersten nicht unsichtbar wird.
  const ranked = [...issues].sort((left, right) => (severityRank[right.severity] ?? 0) - (severityRank[left.severity] ?? 0));
  const primaryIssue = ranked[0];
  const furtherIssues = ranked.slice(1);
  const color = severityColors[primaryIssue.severity] ?? severityColors.critical;
  const titleOf = (issue: CriticalIssueDto) =>
    translateCriticalIssue(language, issue.type ?? issue.code, "title", issue.parameters) ?? issue.title;
  const messageOf = (issue: CriticalIssueDto) =>
    translateCriticalIssue(language, issue.type ?? issue.code, "message", issue.parameters) ?? issue.message;
  const openLink = (issue: CriticalIssueDto) => issue.target && onOpenTarget ? (
    <button
      type="button"
      onClick={() => onOpenTarget(issue.target as string)}
      style={{
        background: "none",
        border: 0,
        padding: 0,
        marginInlineStart: spacing.elementGap,
        color: "inherit",
        font: "inherit",
        textDecoration: "underline",
        cursor: "pointer",
      }}
    >
      {criticalIssueText(language, "open")}
    </button>
  ) : null;

  return (
    <div
      className={className}
      style={{
        padding: spacing.elementGap,
        background: "var(--mq-critical-issue-shell-bg, #050505)",
        borderBottom: `1px solid ${colorBorderSecondary}`,
      }}
    >
      <style>{buildBlinkStyles(color)}</style>
      <div
        role="alert"
        aria-live="assertive"
        className="mq-critical-issue-frame"
        style={{
          border: `var(--mq-critical-issue-border-width, 4px) solid var(--mq-critical-issue-color, ${color})`,
          color: `var(--mq-critical-issue-color, ${color})`,
          background: "var(--mq-critical-issue-bg, #000000)",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
          fontSize: typography.base,
          lineHeight: typography.lineHeightReadable,
          padding: spacing.cardPadding,
          borderRadius: 0,
          display: "grid",
          gap: spacing.elementGap,
          textAlign: "center",
        }}
      >
        <strong style={{ fontSize: typography.titleSm, lineHeight: typography.lineHeightTight }}>
          {titleOf(primaryIssue)}
          {openLink(primaryIssue)}
        </strong>
        {messageOf(primaryIssue) ? (
          <div
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontSize: typography.md,
              opacity: 0.85,
            }}
          >
            {messageOf(primaryIssue)}
          </div>
        ) : null}
        {primaryIssue.technicalDetail && !(primaryIssue.type === FAILURE_CIRCUITS_BLOCKED_TYPE && primaryIssue.parameters?.count === SINGULAR_COUNT) ? (
          <div
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontSize: typography.md,
            }}
          >
            {primaryIssue.technicalDetail}
          </div>
        ) : null}
        {furtherIssues.map((issue) => (
          <div key={issue.id ?? issue.type ?? issue.title} style={{ fontSize: typography.md, opacity: 0.85 }}>
            {titleOf(issue)}
            {openLink(issue)}
          </div>
        ))}
      </div>
    </div>
  );
}
